import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Chip, Link, Popover, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import type {
  VeraGoogleSearchGrounding,
  VeraGroundingCitation,
  VeraGroundingSource,
} from '../models/VeraGoogleSearchGrounding';
import { MarkdownRenderer } from './MarkdownRenderer';
import {
  buildGroundedMessageSegments,
  type GroundedCitationRef,
} from '../utils/veraGroundedMarkdown';

interface VeraGroundedMessageProps {
  text: string;
  grounding: VeraGoogleSearchGrounding | null | undefined;
  markdownVariant?: 'inline' | 'block';
}

function sourceLabel(source: VeraGroundingSource, displayIndex: number): string {
  if (source.title?.trim()) return source.title.trim();
  if (source.domain?.trim()) return source.domain.trim();
  try {
    return new URL(source.uri).hostname.replace(/^www\./, '');
  } catch {
    return `Source ${displayIndex}`;
  }
}

function uriDedupeKey(uri: string): string {
  try {
    const url = new URL(uri);
    url.hash = '';
    return url.href;
  } catch {
    return uri;
  }
}

function dedupeSourcesByUri(
  sources: VeraGroundingSource[],
  displayNumbers: number[],
): Array<{ source: VeraGroundingSource; numbers: number[] }> {
  const groups = new Map<string, { source: VeraGroundingSource; numbers: number[] }>();

  sources.forEach((source, idx) => {
    const key = uriDedupeKey(source.uri);
    const num = displayNumbers[idx] ?? source.index + 1;
    const existing = groups.get(key);
    if (existing) {
      if (!existing.numbers.includes(num)) {
        existing.numbers.push(num);
      }
      if (!existing.source.snippet && source.snippet) {
        existing.source = source;
      }
    } else {
      groups.set(key, { source, numbers: [num] });
    }
  });

  return [...groups.values()].map((group) => ({
    ...group,
    numbers: [...group.numbers].sort((a, b) => a - b),
  }));
}

function CitationChip({
  citation,
  sources,
  displayNumbers,
}: {
  citation: VeraGroundingCitation;
  sources: VeraGroundingSource[];
  displayNumbers: number[];
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const linkedSources = citation.sourceIndices
    .map((idx) => sources.find((source) => source.index === idx))
    .filter((source): source is VeraGroundingSource => !!source);
  const uniqueSources = useMemo(
    () => dedupeSourcesByUri(linkedSources, displayNumbers),
    [linkedSources, displayNumbers],
  );

  return (
    <>
      <Chip
        size="small"
        component="button"
        onClick={(event) => setAnchor(event.currentTarget)}
        label={displayNumbers.join(',')}
        sx={{
          height: 18,
          minWidth: 18,
          mx: 0.25,
          verticalAlign: 'super',
          fontSize: '0.65rem',
          fontWeight: 700,
          cursor: 'pointer',
          '& .MuiChip-label': { px: 0.5 },
        }}
        color="primary"
        variant="outlined"
      />
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { maxWidth: 360, p: 1.5 } } }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          sx={{ display: 'block', mb: 0.75 }}
        >
          Grounded excerpt
        </Typography>
        <Box
          sx={{
            p: 1,
            mb: 1,
            borderRadius: 1,
            bgcolor: '#fff8e1',
            border: '1px solid #ffe082',
            fontSize: '0.85rem',
            lineHeight: 1.45,
            whiteSpace: 'pre-wrap',
          }}
        >
          {citation.text || '—'}
        </Box>
        {uniqueSources.map(({ source, numbers }) => (
          <Box key={uriDedupeKey(source.uri)} sx={{ mb: 0.75 }}>
            <Link
              href={source.uri}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontWeight: 600,
                fontSize: '0.8rem',
              }}
            >
              [{numbers.join(', ')}] {sourceLabel(source, numbers[0])}
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
            {source.snippet && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.25, lineHeight: 1.35 }}
              >
                {source.snippet}
              </Typography>
            )}
          </Box>
        ))}
      </Popover>
    </>
  );
}

const groundedMarkdownSx = {
  fontSize: '0.875rem',
  lineHeight: 1.55,
  wordBreak: 'break-word',
  '& p:last-child': { mb: 0 },
  '& ul, & ol': { my: 0.5, pl: 2.5 },
};

function GroundedMarkdownText({
  text,
  citations,
  sources,
  markdownVariant,
}: {
  text: string;
  citations: VeraGroundingCitation[];
  sources: VeraGroundingSource[];
  markdownVariant: 'inline' | 'block';
}) {
  const segments = useMemo(
    () => buildGroundedMessageSegments(text, citations, sources),
    [citations, sources, text],
  );

  const renderCitationRefs = (refs: GroundedCitationRef[], keyPrefix: string) =>
    refs.map((ref, idx) => (
      <CitationChip
        key={`${keyPrefix}-${idx}-${ref.citation.startIndex}`}
        citation={ref.citation}
        sources={sources}
        displayNumbers={ref.displayNumbers}
      />
    ));

  return (
    <Box sx={groundedMarkdownSx}>
      {segments.map((segment, index) => {
        if (segment.type === 'markdown') {
          if (!segment.content.trim()) return null;
          return (
            <MarkdownRenderer key={`md-${index}`} variant={markdownVariant}>
              {segment.content}
            </MarkdownRenderer>
          );
        }
        if (segment.type === 'lineWithCitations') {
          return (
            <Box
              key={`line-${index}`}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                gap: 0.5,
                mb: 0.5,
              }}
            >
              {segment.content.trim() ? (
                <MarkdownRenderer variant="inline">{segment.content}</MarkdownRenderer>
              ) : null}
              {renderCitationRefs(segment.citations, `line-${index}`)}
            </Box>
          );
        }
        return (
          <Box
            key={`trail-${index}`}
            sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 0.5, mt: 0.5 }}
          >
            {renderCitationRefs(segment.citations, `trail-${index}`)}
          </Box>
        );
      })}
    </Box>
  );
}

/** Renders Vera assistant text with optional Google Search citation chips (investor + admin). */
export default function VeraGroundedMessage({
  text,
  grounding,
  markdownVariant = 'block',
}: VeraGroundedMessageProps) {
  const hasGrounding = !!(grounding?.sources?.length || grounding?.citations?.length);
  const useInlineCitations = !!(grounding?.citations?.length && grounding?.sources?.length);

  if (!hasGrounding || !useInlineCitations) {
    return <MarkdownRenderer variant={markdownVariant}>{text}</MarkdownRenderer>;
  }

  return (
    <GroundedMarkdownText
      text={text}
      citations={grounding.citations}
      sources={grounding.sources}
      markdownVariant={markdownVariant}
    />
  );
}
